import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ModuleRegistry,
  AllCommunityModule,
} from "ag-grid-community";
import { getApiBase } from "../apiBase";
import { isCvinExpansion } from "../projectGridColumns";
import {
  phaseCellClass,
  phaseCellStyle,
  subphaseCellStyle,
} from "../projectPhaseDisplay";
import { isPluginEnabled, PLUGIN_IDS } from "./registry";
import { useBoardTheme } from "./boardThemeStore";
import {
  canEditInterconnectsGrid,
  canManageInterconnects,
  canViewInterconnects,
  interconnectsPluginEnabled,
} from "./interconnectsAccess";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import {
  createInterconnect,
  deleteInterconnect,
  fetchInterconnect,
  fetchInterconnectPartnerOptions,
  fetchInterconnects,
  updateInterconnect,
} from "./interconnectsApi";
import {
  InterconnectFieldRow,
  LinkedProjectPicker,
  SegmentGroupPicker,
} from "./interconnectFormPickers";
import {
  buildInterconnectSegmentGroupMap,
  compareInterconnectSegmentLabels,
  filterLinkableExpansionSubsegments,
  formatInterconnectAssociatedSegmentLabel,
  formatInterconnectSegmentLabel,
  formatInterconnectSegmentOnlyLabel,
  interconnectAssociatedSegmentsText,
  linkedSubsegmentPhaseLabel,
  linkedSubsegmentSegmentLabel,
  linkedSubsegmentSubphaseLabel,
  resolveLinkedSubsegmentRow,
} from "./interconnectsUtils";
import { workOrderBaseForRow } from "./expansionGroupUtils";
import { compareExpansionWorkOrderDisplay } from "./expansionWorkOrderFormat";
import "./interconnectsPage.css";

ModuleRegistry.registerModules([AllCommunityModule]);

function csvEscapeCell(val) {
  if (val == null) return "";
  const s = String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function getDisplayedExportColumnDefs(api, fallbackColumnDefs) {
  const displayed = api?.getAllDisplayedColumns?.() ?? [];
  if (displayed.length) {
    return displayed
      .map((col) => ({
        ...col.getColDef(),
        colId: col.getColId(),
        field: col.getColDef().field,
      }))
      .filter((def) => def.field || typeof def.valueGetter === "function");
  }
  return (fallbackColumnDefs || []).filter(
    (c) => (c.field || typeof c.valueGetter === "function") && c.hide !== true
  );
}

function collectDisplayedGridRows(api) {
  const rows = [];
  api?.forEachNodeAfterFilterAndSort?.((node) => {
    if (node?.data) rows.push(node.data);
  });
  return rows;
}

function exportInterconnectRowsToCsv(rows, columnDefs, gridContext, filename) {
  const cols = (columnDefs || []).filter(
    (c) => c.field || typeof c.valueGetter === "function"
  );
  const headerLine = cols
    .map((c) => csvEscapeCell(c.headerName || c.field || c.colId || ""))
    .join(",");
  const lines = [headerLine];
  for (const row of rows) {
    const cells = cols.map((c) => {
      let v;
      if (typeof c.valueGetter === "function") {
        try {
          v = c.valueGetter({ data: row, context: gridContext });
        } catch {
          v = "";
        }
      } else if (c.field) {
        v = row[c.field];
      } else {
        v = "";
      }
      if (typeof c.valueFormatter === "function") {
        try {
          v = c.valueFormatter({ value: v, data: row, context: gridContext });
        } catch {
          /* keep raw value */
        }
      }
      return csvEscapeCell(v);
    });
    lines.push(cells.join(","));
  }
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function withLinkedProjectData(params) {
  const projectRow = resolveLinkedSubsegmentRow(params.data, params.context?.projectsById);
  if (!projectRow) return null;
  return { ...params, data: projectRow };
}

function interconnectPhaseCellClass(params) {
  const linked = withLinkedProjectData(params);
  return linked ? phaseCellClass(linked) : "phase-cell-default";
}

function interconnectPhaseCellStyle(params) {
  const linked = withLinkedProjectData(params);
  return linked ? phaseCellStyle(linked) : null;
}

function interconnectSubphaseCellStyle(params) {
  const linked = withLinkedProjectData(params);
  return linked ? subphaseCellStyle(linked) : null;
}

function InterconnectTitleCell(props) {
  const id = props.data?.id;
  const label = props.data?.cdtNumber || props.value || "Untitled";
  if (!id) return label;
  return (
    <Link to={`/interconnects/${id}`} className="interconnects-link">
      {label}
    </Link>
  );
}

function AssociatedSegmentsCell(props) {
  const bases = props.data?.associatedSegmentBases || [];
  if (!bases.length) return "—";
  const segmentGroupsByBase = props.context?.segmentGroupsByBase;
  return (
    <span className="interconnects-segments-cell">
      {bases.map((base, index) => (
        <React.Fragment key={base ?? index}>
          {index > 0 ? <span className="interconnects-muted"> · </span> : null}
          <Link to={`/expansion/segment/${encodeURIComponent(base)}`} className="interconnects-link">
            {formatInterconnectAssociatedSegmentLabel(base, segmentGroupsByBase)}
          </Link>
        </React.Fragment>
      ))}
    </span>
  );
}

function SubsegmentCell(props) {
  const project = props.data?.linkedProject;
  if (!project?.id) return "—";
  return (
    <Link to={`/project/${project.id}`} className="interconnects-link">
      {formatInterconnectSegmentLabel(project)}
    </Link>
  );
}

function SegmentCell(props) {
  const interconnect = props.data;
  const { segmentGroupsByBase, projectsById } = props.context || {};
  const projectRow = resolveLinkedSubsegmentRow(interconnect, projectsById);
  if (!projectRow) return "—";
  const base = workOrderBaseForRow(projectRow);
  if (!base) return "—";
  const label = formatInterconnectSegmentOnlyLabel(base, segmentGroupsByBase);
  if (!label) return "—";
  return (
    <Link to={`/expansion/segment/${encodeURIComponent(base)}`} className="interconnects-link">
      {label}
    </Link>
  );
}

function LinkedSubsegmentPhaseCell(props) {
  const label = linkedSubsegmentPhaseLabel(props.data, props.context?.projectsById);
  return label || "—";
}

function LinkedSubsegmentSubphaseCell(props) {
  const label = linkedSubsegmentSubphaseLabel(props.data, props.context?.projectsById);
  return label || "—";
}

function PageShell({ children, variant = "default", creating = false }) {
  const isList = variant === "list";
  return (
    <div
      className={`interconnects-shell${isList ? " interconnects-shell--list" : ""}${
        isList && creating ? " interconnects-shell--creating" : ""
      }`}
    >
      <div
        className={`interconnects-page${isList ? " interconnects-page--list" : ""}${
          isList && creating ? " interconnects-page--creating" : ""
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function emptyDraft() {
  return {
    cdtNumber: "",
    location: "",
    gps: "",
    locationFinalized: false,
    interconnectingPartner: "",
    installPartner: "",
    notes: "",
    linkedProjectId: null,
    associatedSegmentBases: [],
  };
}

function interconnectToDraft(row) {
  if (!row) return emptyDraft();
  return {
    cdtNumber: row.cdtNumber || "",
    location: row.location || "",
    gps: row.gps || "",
    locationFinalized: Boolean(row.locationFinalized),
    interconnectingPartner: row.interconnectingPartner || "",
    installPartner: row.installPartner || "",
    notes: row.notes || "",
    linkedProjectId: row.linkedProjectId ?? row.projectIds?.[0] ?? null,
    associatedSegmentBases: row.associatedSegmentBases || [],
  };
}

function InterconnectFormFields({
  draft,
  setDraft,
  linkableSubsegments,
  segmentGroupList,
  partnerOptions,
  disabled,
  partnerListId,
  installListId,
}) {
  return (
    <div className="commercial-fields interconnects-form-fields">
      <InterconnectFieldRow label="CDT number">
        <input
          className="commercial-field-input"
          value={draft.cdtNumber}
          onChange={(e) => setDraft((d) => ({ ...d, cdtNumber: e.target.value }))}
          disabled={disabled}
        />
      </InterconnectFieldRow>
      <InterconnectFieldRow label="Location">
        <input
          className="commercial-field-input"
          value={draft.location}
          onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
          disabled={disabled}
        />
      </InterconnectFieldRow>
      <InterconnectFieldRow label="GPS" hint="Latitude, longitude (e.g. 37.7749, -122.4194)">
        <input
          className="commercial-field-input"
          value={draft.gps}
          onChange={(e) => setDraft((d) => ({ ...d, gps: e.target.value }))}
          disabled={disabled}
          placeholder="lat, lng"
        />
      </InterconnectFieldRow>
      <InterconnectFieldRow label="Interconnecting partner">
        <input
          className="commercial-field-input"
          value={draft.interconnectingPartner}
          onChange={(e) => setDraft((d) => ({ ...d, interconnectingPartner: e.target.value }))}
          disabled={disabled}
          list={partnerListId}
        />
        <datalist id={partnerListId}>
          {partnerOptions.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      </InterconnectFieldRow>
      <InterconnectFieldRow label="Install partner">
        <input
          className="commercial-field-input"
          value={draft.installPartner}
          onChange={(e) => setDraft((d) => ({ ...d, installPartner: e.target.value }))}
          disabled={disabled}
          list={installListId}
        />
        <datalist id={installListId}>
          {partnerOptions.map((o) => (
            <option key={o} value={o} />
          ))}
        </datalist>
      </InterconnectFieldRow>
      <InterconnectFieldRow label="Notes">
        <textarea
          className="commercial-field-textarea interconnects-notes-textarea"
          value={draft.notes}
          onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
          disabled={disabled}
          rows={3}
          placeholder="Interconnect notes from planning or field work"
        />
      </InterconnectFieldRow>
      <InterconnectFieldRow label="Linked work order" hint="One expansion subsegment (dotted work order).">
        <LinkedProjectPicker
          projects={linkableSubsegments}
          selectedId={draft.linkedProjectId}
          onChange={(linkedProjectId) => setDraft((d) => ({ ...d, linkedProjectId }))}
          disabled={disabled}
        />
      </InterconnectFieldRow>
      <InterconnectFieldRow
        label="Associated segments"
        hint="Parent expansion segments related to this interconnect (not subsegments)."
      >
        <SegmentGroupPicker
          segmentGroups={segmentGroupList}
          selectedBases={draft.associatedSegmentBases}
          onChange={(associatedSegmentBases) =>
            setDraft((d) => ({ ...d, associatedSegmentBases }))
          }
          disabled={disabled}
        />
      </InterconnectFieldRow>
      <InterconnectFieldRow label="Location finalized">
        <label className="commercial-multiselect-option">
          <input
            type="checkbox"
            checked={draft.locationFinalized}
            onChange={(e) => setDraft((d) => ({ ...d, locationFinalized: e.target.checked }))}
            disabled={disabled}
          />
          Location is finalized
        </label>
      </InterconnectFieldRow>
    </div>
  );
}

function InterconnectsListPage({ apiUrl, getAuthHeaders, canEdit, canEditGrid }) {
  const { agGridTheme } = useBoardTheme();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [gridSaveError, setGridSaveError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [linkableSubsegments, setLinkableSubsegments] = useState([]);
  const [expansionProjects, setExpansionProjects] = useState([]);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [displayedExportCount, setDisplayedExportCount] = useState(0);
  const gridApiRef = useRef(null);

  const segmentGroups = useMemo(
    () => buildInterconnectSegmentGroupMap(expansionProjects),
    [expansionProjects]
  );
  const segmentGroupList = useMemo(() => [...segmentGroups.values()], [segmentGroups]);
  const projectsById = useMemo(() => {
    const map = new Map();
    for (const row of expansionProjects) {
      if (row?.id != null) map.set(Number(row.id), row);
    }
    return map;
  }, [expansionProjects]);
  const gridContext = useMemo(
    () => ({ segmentGroupsByBase: segmentGroups, projectsById }),
    [segmentGroups, projectsById]
  );

  const syncDisplayedExportCount = useCallback((api) => {
    const gridApi = api || gridApiRef.current;
    if (!gridApi) {
      setDisplayedExportCount(0);
      return;
    }
    setDisplayedExportCount(collectDisplayedGridRows(gridApi).length);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchInterconnects({ apiUrl, getAuthHeaders });
      setRows(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    syncDisplayedExportCount();
  }, [rows, syncDisplayedExportCount]);

  useEffect(() => {
    if (!canEditGrid) return undefined;
    let cancelled = false;
    fetchInterconnectPartnerOptions({ apiUrl, getAuthHeaders })
      .then((opts) => {
        if (!cancelled) setPartnerOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setPartnerOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, canEditGrid, getAuthHeaders]);

  useEffect(() => {
    if (!showCreate) return undefined;
    let cancelled = false;
    const base = apiUrl || getApiBase();
    Promise.all([
      axios.get(`${base}/projects`, { headers: getAuthHeaders?.() }),
      fetchInterconnectPartnerOptions({ apiUrl, getAuthHeaders }),
    ])
      .then(([projRes, opts]) => {
        if (cancelled) return;
        const subsegmentRows = filterLinkableExpansionSubsegments(projRes.data || []);
        const expansionRows = (projRes.data || []).filter((r) => isCvinExpansion(r));
        setLinkableSubsegments(subsegmentRows);
        setExpansionProjects(expansionRows);
        setPartnerOptions(opts);
      })
      .catch(() => {
        if (!cancelled) {
          setLinkableSubsegments([]);
          setExpansionProjects([]);
          setPartnerOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showCreate, apiUrl, getAuthHeaders]);

  useEffect(() => {
    let cancelled = false;
    const base = apiUrl || getApiBase();
    axios
      .get(`${base}/projects`, { headers: getAuthHeaders?.() })
      .then((res) => {
        if (cancelled) return;
        const expansionRows = (res.data || []).filter((r) => isCvinExpansion(r));
        setExpansionProjects(expansionRows);
      })
      .catch(() => {
        if (!cancelled) setExpansionProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, getAuthHeaders]);

  const onGridCellValueChanged = useCallback(
    async (event) => {
      if (!canEditGrid) return;
      const row = event.data;
      const field = event.colDef?.field;
      if (!row?.id || !field || field === "updatedAt") return;

      const patchBody = { _source: "interconnects_grid" };
      if (field === "locationFinalized") {
        patchBody.locationFinalized = Boolean(event.newValue);
      } else {
        patchBody[field] = event.newValue ?? "";
      }

      setGridSaveError("");
      try {
        const updated = await updateInterconnect(row.id, patchBody, { apiUrl, getAuthHeaders });
        setRows((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } catch (err) {
        if (event.node?.setDataValue) {
          event.node.setDataValue(field, event.oldValue, false);
        }
        setGridSaveError(
          err.response?.data?.error || err.message || "Could not save interconnect change."
        );
      }
    },
    [apiUrl, canEditGrid, getAuthHeaders]
  );

  const columnDefs = useMemo(
    () => [
      {
        colId: "linkedSegment",
        headerName: "Segment",
        flex: 1,
        minWidth: 100,
        cellRenderer: SegmentCell,
        valueGetter: (p) =>
          linkedSubsegmentSegmentLabel(p.data, segmentGroups, projectsById),
        comparator: compareInterconnectSegmentLabels,
      },
      {
        colId: "linkedSubsegment",
        headerName: "Subsegment",
        flex: 1,
        minWidth: 120,
        cellRenderer: SubsegmentCell,
        valueGetter: (p) =>
          p.data?.linkedProject ? formatInterconnectSegmentLabel(p.data.linkedProject) : "",
        comparator: compareExpansionWorkOrderDisplay,
      },
      {
        field: "cdtNumber",
        headerName: "CDT number",
        width: 130,
        editable: canEditGrid,
        cellRenderer: InterconnectTitleCell,
      },
      {
        colId: "phase",
        headerName: "Phase",
        width: 120,
        cellRenderer: LinkedSubsegmentPhaseCell,
        valueGetter: (p) => linkedSubsegmentPhaseLabel(p.data, projectsById),
        cellClass: interconnectPhaseCellClass,
        cellStyle: interconnectPhaseCellStyle,
      },
      {
        colId: "subphase",
        headerName: "Subphase",
        flex: 1,
        minWidth: 140,
        cellRenderer: LinkedSubsegmentSubphaseCell,
        valueGetter: (p) => linkedSubsegmentSubphaseLabel(p.data, projectsById),
        cellStyle: interconnectSubphaseCellStyle,
      },
      { field: "location", headerName: "Location", flex: 1, minWidth: 160, editable: canEditGrid },
      { field: "gps", headerName: "GPS", width: 150, editable: canEditGrid },
      {
        field: "interconnectingPartner",
        headerName: "Interconnecting partner",
        flex: 1,
        minWidth: 140,
        editable: canEditGrid,
      },
      { field: "installPartner", headerName: "Install partner", flex: 1, minWidth: 120, editable: canEditGrid },
      {
        colId: "associatedSegments",
        headerName: "Associated segments",
        flex: 1.2,
        minWidth: 200,
        cellRenderer: AssociatedSegmentsCell,
        autoHeight: true,
        wrapText: true,
        valueGetter: (p) =>
          interconnectAssociatedSegmentsText(p.data?.associatedSegmentBases, segmentGroups),
      },
    ],
    [canEditGrid, segmentGroups, projectsById]
  );

  const handleExport = useCallback(() => {
    const api = gridApiRef.current;
    const exportRows = collectDisplayedGridRows(api);
    const cols = getDisplayedExportColumnDefs(api, columnDefs);
    exportInterconnectRowsToCsv(exportRows, cols, gridContext, "interconnects.csv");
  }, [columnDefs, gridContext]);

  const onCreate = async () => {
    setSaving(true);
    setError("");
    try {
      await createInterconnect(
        {
          cdtNumber: draft.cdtNumber,
          location: draft.location,
          gps: draft.gps,
          locationFinalized: draft.locationFinalized,
          interconnectingPartner: draft.interconnectingPartner,
          installPartner: draft.installPartner,
          notes: draft.notes,
          linkedProjectId: draft.linkedProjectId,
          associatedSegmentBases: draft.associatedSegmentBases,
        },
        { apiUrl, getAuthHeaders }
      );
      setShowCreate(false);
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell variant="list" creating={showCreate}>
      <header className="interconnects-page-header">
        <div>
          <h1 className="interconnects-page-title">Interconnects</h1>
          <p className="interconnects-page-lead">
            CDT interconnect locations linked to one expansion subsegment work order, with optional
            associations to parent expansion segments.
            {canEditGrid ? " Double-click a cell to edit (admins and drafters)." : null}
          </p>
        </div>
        <div className="interconnects-page-header-actions">
          <button
            type="button"
            className="interconnects-btn"
            disabled={loading || displayedExportCount === 0}
            onClick={handleExport}
          >
            Export ({displayedExportCount})
          </button>
          {canEdit ? (
            <button
              type="button"
              className="interconnects-btn interconnects-btn--primary"
              onClick={() => setShowCreate(true)}
            >
              New interconnect
            </button>
          ) : null}
        </div>
      </header>

      {error ? <p className="project-detail-error">{error}</p> : null}
      {gridSaveError ? <p className="project-detail-error">{gridSaveError}</p> : null}

      {showCreate ? (
        <div className="interconnects-panel interconnects-create-panel">
          <h2 className="interconnects-panel-title">New CDT interconnect</h2>
          <InterconnectFormFields
            draft={draft}
            setDraft={setDraft}
            linkableSubsegments={linkableSubsegments}
            segmentGroupList={segmentGroupList}
            partnerOptions={partnerOptions}
            partnerListId="ic-partner-create"
            installListId="ic-install-create"
          />
          <div className="interconnects-form-actions">
            <button
              type="button"
              className="interconnects-btn interconnects-btn--primary"
              onClick={onCreate}
              disabled={saving}
            >
              {saving ? "Creating…" : "Create"}
            </button>
            <button
              type="button"
              className="interconnects-btn"
              onClick={() => {
                setShowCreate(false);
                setDraft(emptyDraft());
                setError("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {!showCreate ? (
      <div className="interconnects-panel interconnects-grid-panel">
        {loading ? (
          <p className="interconnects-muted" style={{ padding: "1rem" }}>
            Loading…
          </p>
        ) : (
          <div className="interconnects-grid-wrap grid-wrap">
            <AgGridReact
              theme={agGridTheme}
              rowData={rows}
              columnDefs={columnDefs}
              context={gridContext}
              getRowId={(p) => String(p.data?.id)}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              stopEditingWhenCellsLoseFocus
              onCellValueChanged={onGridCellValueChanged}
              onGridReady={(params) => {
                gridApiRef.current = params.api;
                syncDisplayedExportCount(params.api);
              }}
              onFilterChanged={(params) => syncDisplayedExportCount(params.api)}
              onSortChanged={(params) => syncDisplayedExportCount(params.api)}
              onModelUpdated={(params) => syncDisplayedExportCount(params.api)}
            />
          </div>
        )}
      </div>
      ) : null}
    </PageShell>
  );
}

function InterconnectDetailPage({ apiUrl, getAuthHeaders, canEdit }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [linkableSubsegments, setLinkableSubsegments] = useState([]);
  const [expansionProjects, setExpansionProjects] = useState([]);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const segmentGroups = useMemo(
    () => buildInterconnectSegmentGroupMap(expansionProjects),
    [expansionProjects]
  );
  const segmentGroupList = useMemo(() => [...segmentGroups.values()], [segmentGroups]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchInterconnect(id, { apiUrl, getAuthHeaders });
      setRow(data);
      setDraft(interconnectToDraft(data));
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [id, apiUrl, getAuthHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const base = apiUrl || getApiBase();
    Promise.all([
      axios.get(`${base}/projects`, { headers: getAuthHeaders?.() }),
      fetchInterconnectPartnerOptions({ apiUrl, getAuthHeaders }),
    ])
      .then(([projRes, opts]) => {
        if (cancelled) return;
        const subsegmentRows = filterLinkableExpansionSubsegments(projRes.data || []);
        const expansionRows = (projRes.data || []).filter((r) => isCvinExpansion(r));
        setLinkableSubsegments(subsegmentRows);
        setExpansionProjects(expansionRows);
        setPartnerOptions(opts);
      })
      .catch(() => {
        if (!cancelled) {
          setLinkableSubsegments([]);
          setExpansionProjects([]);
          setPartnerOptions([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, getAuthHeaders]);

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      await updateInterconnect(
        id,
        {
          cdtNumber: draft.cdtNumber,
          location: draft.location,
          gps: draft.gps,
          locationFinalized: draft.locationFinalized,
          interconnectingPartner: draft.interconnectingPartner,
          installPartner: draft.installPartner,
          notes: draft.notes,
          linkedProjectId: draft.linkedProjectId,
          associatedSegmentBases: draft.associatedSegmentBases,
        },
        { apiUrl, getAuthHeaders }
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this interconnect record? The linked project will be unlinked.")) return;
    try {
      await deleteInterconnect(id, { apiUrl, getAuthHeaders });
      navigate("/interconnects");
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Delete failed");
    }
  };

  if (loading) {
    return (
      <PageShell>
        <p className="interconnects-muted">Loading…</p>
      </PageShell>
    );
  }

  if (!row) {
    return (
      <PageShell>
        <p className="project-detail-error">{error || "Interconnect not found."}</p>
        <Link to="/interconnects" className="interconnects-link">
          Back to list
        </Link>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <header className="interconnects-page-header">
        <div>
          <p style={{ margin: "0 0 0.35rem" }}>
            <Link to="/interconnects" className="interconnects-link">
              ← Interconnects
            </Link>
          </p>
          <h1 className="interconnects-page-title">{row.cdtNumber || `Interconnect #${row.id}`}</h1>
        </div>
        {canEdit ? (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              className="interconnects-btn interconnects-btn--primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button type="button" className="interconnects-btn contracts-btn--danger" onClick={onDelete}>
              Delete
            </button>
          </div>
        ) : null}
      </header>

      {error ? <p className="project-detail-error">{error}</p> : null}

      <div className="interconnects-panel">
        <InterconnectFormFields
          draft={draft}
          setDraft={setDraft}
          linkableSubsegments={linkableSubsegments}
          segmentGroupList={segmentGroupList}
          partnerOptions={partnerOptions}
          disabled={!canEdit}
          partnerListId="ic-partner-detail"
          installListId="ic-install-detail"
        />
      </div>
    </PageShell>
  );
}

export function InterconnectsGate({ apiUrl, getAuthHeaders }) {
  const { id } = useParams();
  const viewerEmail = getViewerEmailFromStorage();

  if (!interconnectsPluginEnabled()) {
    return <Navigate to="/" replace />;
  }
  if (!canViewInterconnects(viewerEmail)) {
    return (
      <PageShell>
        <p className="project-detail-error">
          You do not have permission to view interconnects. Engineering or project management roles are
          required.
        </p>
      </PageShell>
    );
  }

  const canEdit = canManageInterconnects(viewerEmail);
  const canEditGrid = canEditInterconnectsGrid(viewerEmail);
  if (id) {
    return <InterconnectDetailPage apiUrl={apiUrl} getAuthHeaders={getAuthHeaders} canEdit={canEdit} />;
  }
  return (
    <InterconnectsListPage
      apiUrl={apiUrl}
      getAuthHeaders={getAuthHeaders}
      canEdit={canEdit}
      canEditGrid={canEditGrid}
    />
  );
}
